/**
 * IndexAddTypeField — presentational sub of `IndexAddFormBody` covering the
 * Slice C4 specialized index-type dropdown plus the per-kind sub-fields:
 *   - mssql FULLTEXT: Key Index (required) + Catalog (optional) text inputs.
 *   - oracle DOMAIN: INDEXTYPE IS … (required) text input.
 *
 * The dropdown options come from `getIndexTypeOptionsForEngine` — kinds
 * outside the engine support set are hidden so users cannot pick a
 * combination the synthesizer would reject. mssql `columnstore` is split into
 * two user-facing options (CLUSTERED COLUMNSTORE vs NONCLUSTERED COLUMNSTORE)
 * because the underlying `IndexType` carries a `clustered?: boolean` flag.
 *
 * On sqlite the dropdown is hidden entirely — sqlite supports only the default
 * b-tree access method, so a single-option dropdown would be UI noise.
 *
 * Orchestrator owns state; this component renders the controls and propagates
 * change events through setters. Mirrors the `IndexAddEngineFields` shape
 * (Slice C1–C3 split pattern carried forward).
 *
 * PRD: Ralph/VqeDdlEdit/PRD.md § Slice C4.
 */
import type { SqlEngine } from '../../../../sql-grammar/model';
import { Input } from '../../../../../components/ui/input';
import { Label } from '../../../../../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../../../../components/ui/select';
import { isIndexTypeDropdownMeaningful } from '../../edit/edits/addIndex';
import {
  getIndexTypeOptionsForEngine,
  type IndexTypeSelectValue,
} from './indexTypeOptions';

/** Radix Select forbids `value=""`; the "Default (b-tree)" option (whose
 *  `IndexTypeSelectValue` is `''`) rides this sentinel through the widget. */
const UNSET = '__unset__' as const;

export interface IndexAddTypeFieldProps {
  readonly testId: string;
  readonly engine: SqlEngine;
  readonly value: IndexTypeSelectValue;
  readonly setValue: (v: IndexTypeSelectValue) => void;
  readonly mssqlKeyIndex: string;
  readonly setMssqlKeyIndex: (s: string) => void;
  readonly mssqlCatalog: string;
  readonly setMssqlCatalog: (s: string) => void;
  readonly oracleIndextype: string;
  readonly setOracleIndextype: (s: string) => void;
}

export function IndexAddTypeField(p: IndexAddTypeFieldProps) {
  if (!isIndexTypeDropdownMeaningful(p.engine)) return null;
  const options = getIndexTypeOptionsForEngine(p.engine);
  const showFulltextExtras = p.engine === 'mssql' && p.value === 'fulltext';
  const showDomainExtras = p.engine === 'oracle' && p.value === 'domain';

  return (
    <>
      <div className="flex flex-col gap-1">
        <Label htmlFor={`${p.testId}-type`} className="text-xs">
          Type
        </Label>
        <Select
          value={p.value === '' ? UNSET : p.value}
          onValueChange={(v) => p.setValue(v === UNSET ? '' : (v as IndexTypeSelectValue))}
        >
          <SelectTrigger
            id={`${p.testId}-type`}
            data-testid={`${p.testId}-type`}
            className="h-8 font-mono text-xs"
          >
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {options.map((opt) => (
              <SelectItem key={opt.value} value={opt.value === '' ? UNSET : opt.value}>
                {opt.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <span className="text-[10px] text-muted-foreground">
          Specialized access method. Default = engine b-tree.
        </span>
      </div>

      {showFulltextExtras ? (
        <>
          <div className="flex flex-col gap-1">
            <Label htmlFor={`${p.testId}-fulltext-key`} className="text-xs">
              Key Index <span className="text-destructive">*</span>
            </Label>
            <Input
              id={`${p.testId}-fulltext-key`}
              data-testid={`${p.testId}-fulltext-key`}
              value={p.mssqlKeyIndex}
              onChange={(e) => p.setMssqlKeyIndex(e.target.value)}
              placeholder="pk_articles"
              className="h-8 font-mono text-xs"
            />
            <span className="text-[10px] text-muted-foreground">
              mssql `KEY INDEX` — the unique key index used to identify rows in the catalog.
            </span>
          </div>
          <div className="flex flex-col gap-1">
            <Label htmlFor={`${p.testId}-fulltext-catalog`} className="text-xs">
              Catalog
            </Label>
            <Input
              id={`${p.testId}-fulltext-catalog`}
              data-testid={`${p.testId}-fulltext-catalog`}
              value={p.mssqlCatalog}
              onChange={(e) => p.setMssqlCatalog(e.target.value)}
              placeholder="ft_catalog"
              className="h-8 font-mono text-xs"
            />
            <span className="text-[10px] text-muted-foreground">
              Optional `ON &lt;catalog&gt;` clause.
            </span>
          </div>
        </>
      ) : null}

      {showDomainExtras ? (
        <div className="flex flex-col gap-1">
          <Label htmlFor={`${p.testId}-domain-indextype`} className="text-xs">
            INDEXTYPE IS <span className="text-destructive">*</span>
          </Label>
          <Input
            id={`${p.testId}-domain-indextype`}
            data-testid={`${p.testId}-domain-indextype`}
            value={p.oracleIndextype}
            onChange={(e) => p.setOracleIndextype(e.target.value)}
            placeholder="CTXSYS.CONTEXT"
            className="h-8 font-mono text-xs"
          />
          <span className="text-[10px] text-muted-foreground">
            oracle domain-index type (e.g. `CTXSYS.CONTEXT`, `MDSYS.SPATIAL_INDEX`).
          </span>
        </div>
      ) : null}
    </>
  );
}
